export class InputController {
  private readonly down = new Set<string>();
  private readonly pressed = new Set<string>();
  private touchX = 0;
  private touchY = 0;
  private lookX = 0;
  private lookY = 0;
  private lookTarget?: HTMLElement;
  private lookPointer?: number;
  private lastLookPoint = { x: 0, y: 0 };
  private joystickPointer?: number;
  private joystickOrigin = { x: 0, y: 0 };
  private joystick?: HTMLElement;
  private joystickKnob?: HTMLElement;
  private actionButton?: HTMLElement;
  private sprintButton?: HTMLElement;
  private vehicleButton?: HTMLElement;
  private actionPointer?: number;
  private sprintPointer?: number;
  private vehiclePointer?: number;

  constructor(private readonly root: HTMLElement) {
    window.addEventListener("keydown", this.onKeyDown, { passive: false });
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onWindowBlur);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    document.addEventListener("mousemove", this.onMouseMove);
    this.bindTouchControls();
  }

  bindLookTarget(target: HTMLElement): void {
    this.lookTarget = target;
    target.addEventListener("contextmenu", (event) => event.preventDefault());
    target.addEventListener("pointerdown", this.onLookPointerDown);
    target.addEventListener("pointermove", this.onLookPointerMove);
    target.addEventListener("pointerup", this.onLookPointerUp);
    target.addEventListener("pointercancel", this.onLookPointerCancel);
    target.addEventListener("lostpointercapture", this.onLookPointerCancel);
  }

  requestPointerLock(): void {
    if (!this.lookTarget || !window.matchMedia("(pointer: fine)").matches) return;
    this.clearLook();
    try {
      void this.lookTarget.requestPointerLock?.().catch(() => this.clearLook());
    } catch {
      this.clearLook();
    }
  }

  releasePointerLock(): void {
    this.clearLook();
    if (document.pointerLockElement) {
      try {
        document.exitPointerLock?.();
      } catch {
        this.clearLook();
      }
    }
  }

  get pointerLocked(): boolean {
    return document.pointerLockElement === this.lookTarget;
  }

  get movement(): { x: number; y: number } {
    let x = this.touchX;
    let y = this.touchY;
    if (this.isDown("KeyA") || this.isDown("ArrowLeft")) x -= 1;
    if (this.isDown("KeyD") || this.isDown("ArrowRight")) x += 1;
    if (this.isDown("KeyW") || this.isDown("ArrowUp")) y -= 1;
    if (this.isDown("KeyS") || this.isDown("ArrowDown")) y += 1;
    const length = Math.hypot(x, y);
    if (length > 1) {
      x /= length;
      y /= length;
    }
    return { x, y };
  }

  get running(): boolean {
    return (
      this.isDown("ShiftLeft") ||
      this.isDown("ShiftRight") ||
      this.isDown("TouchSprint")
    );
  }

  isDown(code: string): boolean {
    return this.down.has(code);
  }

  consume(code: string): boolean {
    if (!this.pressed.has(code)) return false;
    this.pressed.delete(code);
    return true;
  }

  consumeLook(): { x: number; y: number } {
    const result = { x: this.lookX, y: this.lookY };
    this.clearLook();
    return result;
  }

  clearLook(): void {
    this.lookX = 0;
    this.lookY = 0;
  }

  clearPressed(): void {
    this.pressed.clear();
  }

  clearAll(): void {
    const lookPointer = this.lookPointer;
    const joystickPointer = this.joystickPointer;
    const actionPointer = this.actionPointer;
    const sprintPointer = this.sprintPointer;
    const vehiclePointer = this.vehiclePointer;
    this.lookPointer = undefined;
    this.joystickPointer = undefined;
    this.actionPointer = undefined;
    this.sprintPointer = undefined;
    this.vehiclePointer = undefined;

    this.releasePointerCapture(this.lookTarget, lookPointer);
    this.releasePointerCapture(this.joystick, joystickPointer);
    this.releasePointerCapture(this.actionButton, actionPointer);
    this.releasePointerCapture(this.sprintButton, sprintPointer);
    this.releasePointerCapture(this.vehicleButton, vehiclePointer);
    this.down.clear();
    this.pressed.clear();
    this.touchX = 0;
    this.touchY = 0;
    this.clearLook();
    this.lastLookPoint = { x: 0, y: 0 };
    if (this.joystickKnob) {
      this.joystickKnob.style.transform = "translate(-50%, -50%)";
    }
    this.actionButton?.classList.remove("is-pressed");
    this.sprintButton?.classList.remove("is-pressed");
    this.vehicleButton?.classList.remove("is-pressed");
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (
      ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Space"].includes(
        event.code,
      )
    ) {
      event.preventDefault();
    }
    if (!this.down.has(event.code)) {
      this.pressed.add(event.code);
    }
    this.down.add(event.code);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.down.delete(event.code);
  };

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (!this.pointerLocked) return;
    if (!Number.isFinite(event.movementX) || !Number.isFinite(event.movementY)) return;
    this.lookX += event.movementX;
    this.lookY += event.movementY;
  };

  /**
   * Touch always drags to look. A mouse only does so while the pointer is not
   * locked, which keeps the game playable when the browser refuses the lock or
   * the player turned the capture off.
   */
  private readonly onLookPointerDown = (event: PointerEvent): void => {
    if (event.pointerType === "mouse" && (this.pointerLocked || event.button !== 0)) return;
    if (this.lookPointer !== undefined) return;
    event.preventDefault();
    this.lookPointer = event.pointerId;
    this.lastLookPoint = { x: event.clientX, y: event.clientY };
    this.lookTarget?.setPointerCapture(event.pointerId);
  };

  private readonly onLookPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.lookPointer) return;
    if (this.pointerLocked) return;
    const dx = event.clientX - this.lastLookPoint.x;
    const dy = event.clientY - this.lastLookPoint.y;
    if (Number.isFinite(dx) && Number.isFinite(dy)) {
      this.lookX += dx;
      this.lookY += dy;
    }
    this.lastLookPoint = { x: event.clientX, y: event.clientY };
  };

  private readonly onLookPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.lookPointer) return;
    this.lookPointer = undefined;
    this.releasePointerCapture(this.lookTarget, event.pointerId);
  };

  private readonly onLookPointerCancel = (event: PointerEvent): void => {
    if (event.pointerId !== this.lookPointer) return;
    this.lookPointer = undefined;
    this.clearLook();
  };

  private bindTouchControls(): void {
    this.joystick =
      this.root.querySelector<HTMLElement>("[data-joystick]") ?? undefined;
    this.joystickKnob =
      this.root.querySelector<HTMLElement>("[data-joystick-knob]") ?? undefined;
    this.actionButton =
      this.root.querySelector<HTMLElement>("[data-touch-action]") ?? undefined;
    this.sprintButton =
      this.root.querySelector<HTMLElement>("[data-touch-sprint]") ?? undefined;
    this.vehicleButton =
      this.root.querySelector<HTMLElement>("[data-touch-vehicle]") ?? undefined;
    const joystick = this.joystick;
    const knob = this.joystickKnob;
    const action = this.actionButton;
    const sprint = this.sprintButton;
    const vehicle = this.vehicleButton;

    joystick?.addEventListener("pointerdown", (event) => {
      if (this.joystickPointer !== undefined) return;
      event.preventDefault();
      this.joystickPointer = event.pointerId;
      joystick.setPointerCapture(event.pointerId);
      const rect = joystick.getBoundingClientRect();
      this.joystickOrigin = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
      this.updateJoystick(event.clientX, event.clientY, knob);
    });

    joystick?.addEventListener("pointermove", (event) => {
      if (event.pointerId !== this.joystickPointer) return;
      this.updateJoystick(event.clientX, event.clientY, knob);
    });

    const releaseJoystick = (event: PointerEvent): void => {
      if (event.pointerId !== this.joystickPointer) return;
      this.joystickPointer = undefined;
      this.releasePointerCapture(joystick, event.pointerId);
      this.touchX = 0;
      this.touchY = 0;
      if (knob) knob.style.transform = "translate(-50%, -50%)";
    };

    joystick?.addEventListener("pointerup", releaseJoystick);
    joystick?.addEventListener("pointercancel", releaseJoystick);
    joystick?.addEventListener("lostpointercapture", releaseJoystick);

    action?.addEventListener("pointerdown", (event) => {
      if (this.actionPointer !== undefined) return;
      event.preventDefault();
      this.actionPointer = event.pointerId;
      action.setPointerCapture(event.pointerId);
      this.pressed.add("KeyE");
      this.down.add("KeyE");
      action.classList.add("is-pressed");
    });
    const releaseAction = (event: PointerEvent): void => {
      if (event.pointerId !== this.actionPointer) return;
      this.actionPointer = undefined;
      this.releasePointerCapture(action, event.pointerId);
      this.down.delete("KeyE");
      action?.classList.remove("is-pressed");
    };
    action?.addEventListener("pointerup", releaseAction);
    action?.addEventListener("pointercancel", releaseAction);
    action?.addEventListener("lostpointercapture", releaseAction);

    sprint?.addEventListener("pointerdown", (event) => {
      if (this.sprintPointer !== undefined) return;
      event.preventDefault();
      this.sprintPointer = event.pointerId;
      sprint.setPointerCapture(event.pointerId);
      this.down.add("TouchSprint");
      sprint.classList.add("is-pressed");
    });
    const releaseSprint = (event: PointerEvent): void => {
      if (event.pointerId !== this.sprintPointer) return;
      this.sprintPointer = undefined;
      this.releasePointerCapture(sprint, event.pointerId);
      this.down.delete("TouchSprint");
      sprint?.classList.remove("is-pressed");
    };
    sprint?.addEventListener("pointerup", releaseSprint);
    sprint?.addEventListener("pointercancel", releaseSprint);
    sprint?.addEventListener("lostpointercapture", releaseSprint);

    // Monter dans le véhicule / en sortir : même touche que F au clavier.
    vehicle?.addEventListener("pointerdown", (event) => {
      if (this.vehiclePointer !== undefined) return;
      event.preventDefault();
      this.vehiclePointer = event.pointerId;
      vehicle.setPointerCapture(event.pointerId);
      this.pressed.add("KeyF");
      this.down.add("KeyF");
      vehicle.classList.add("is-pressed");
    });
    const releaseVehicle = (event: PointerEvent): void => {
      if (event.pointerId !== this.vehiclePointer) return;
      this.vehiclePointer = undefined;
      this.releasePointerCapture(vehicle, event.pointerId);
      this.down.delete("KeyF");
      vehicle?.classList.remove("is-pressed");
    };
    vehicle?.addEventListener("pointerup", releaseVehicle);
    vehicle?.addEventListener("pointercancel", releaseVehicle);
    vehicle?.addEventListener("lostpointercapture", releaseVehicle);
  }

  private updateJoystick(
    clientX: number,
    clientY: number,
    knob?: HTMLElement | null,
  ): void {
    const max = 38;
    let dx = clientX - this.joystickOrigin.x;
    let dy = clientY - this.joystickOrigin.y;
    const length = Math.hypot(dx, dy);
    if (length > max) {
      dx = (dx / length) * max;
      dy = (dy / length) * max;
    }
    this.touchX = dx / max;
    this.touchY = dy / max;
    if (knob) {
      knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    }
  }

  private readonly onWindowBlur = (): void => {
    this.clearAll();
  };

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) this.clearAll();
  };

  private releasePointerCapture(
    target?: HTMLElement,
    pointerId?: number,
  ): void {
    if (
      pointerId === undefined ||
      !target?.hasPointerCapture?.(pointerId)
    ) {
      return;
    }
    try {
      target.releasePointerCapture(pointerId);
    } catch {
      // The browser may have already released capture during cancellation.
    }
  }
}
